/**
 * Bo+Tee navigation — product-tile carousel + mobile L2 overlay.
 * Works with header-drawer.js for open/close of the sheet.
 */

const CAROUSEL_SELECTOR = '[data-bt-nav-tiles]';
const DRAWER_SELECTOR = '[data-bt-nav-drawer]';
const MEGA_COLS = 6;
const TILE_GAP_PX = 1;
/** Figma 8377:13254 / 8380:6678 — collection card width so height stays 116:181. */
const CARD_WIDTH_PX = 231.5;
/** Allow a slight shrink so 4 menus + 2 cards still fit around a 1440px row. */
const MIN_CARD_WIDTH_PX = 180;
/** Figma mobile L2: 3 full cards; 4+ enables a slider with a 4th-card peek. */
const MOBILE_VISIBLE_COUNT = 3;

/**
 * @param {HTMLElement} root
 */
function visibleTileItems(root) {
  const inDrawer = Boolean(root.closest('.bt-nav-drawer'));
  return [...root.querySelectorAll('[data-bt-nav-tiles-item]')].filter((el) => {
    if (inDrawer && el.classList.contains('bt-nav-tiles__item--desktop-only')) return false;
    return true;
  });
}

/**
 * Merchant setting — desktop fallback when leftover-column math cannot run.
 * @param {HTMLElement} root
 */
function configuredVisibleCount(root) {
  return Math.max(1, Number(root.dataset.visible) || 2);
}

/**
 * @param {HTMLElement} menu
 */
function countMenuColumns(menu) {
  return [...menu.querySelectorAll('.bt-mega-menu__column')].filter((el) => {
    return el instanceof HTMLElement && getComputedStyle(el).display !== 'none';
  }).length;
}

/**
 * Leftover columns after menus in the 6-column desktop row.
 * @param {HTMLElement} menu
 */
function leftoverColumns(menu) {
  return Math.max(1, MEGA_COLS - countMenuColumns(menu));
}

/**
 * Width available for right-aligned collection cards (menus stay 204px).
 * Measures from the last menu column to the content-box right so parent
 * padding / subpixels do not drop a card that still fits.
 * @param {HTMLElement} menu
 */
function leftoverTilesWidth(menu) {
  const style = getComputedStyle(menu);
  const gap = parseFloat(style.gap) || 24;
  const padRight = parseFloat(style.paddingRight) || 0;
  const menuRect = menu.getBoundingClientRect();
  const contentLeft = menuRect.left + (parseFloat(style.paddingLeft) || 0);
  let lastColRight = contentLeft;
  let columns = 0;
  menu.querySelectorAll('.bt-mega-menu__column').forEach((el) => {
    if (!(el instanceof HTMLElement) || getComputedStyle(el).display === 'none') return;
    lastColRight = Math.max(lastColRight, el.getBoundingClientRect().right);
    columns += 1;
  });
  const trailingGap = columns > 0 ? gap : 0;
  return Math.max(0, menuRect.right - padRight - lastColRight - trailingGap);
}

/**
 * How many collection cards to show, and at what width.
 * Desktop: leftover columns after menus, Figma 231.5px when it fits.
 * Cards may shrink (floor 180px) so 4 menus + 2 images still land at ~1440.
 * 4 menus + extra cards → 2 cards + slider
 * 2 menus + 3 cards → 3 cards, no slider
 * Mobile: 3 cards fill the row; 4+ → slider with a 4th-card peek.
 * @param {HTMLElement} root
 * @param {number} itemCount
 * @returns {{ visible: number, tileWidth: number }}
 */
function desktopTileLayout(root, itemCount) {
  const configured = configuredVisibleCount(root);
  if (itemCount < 1) return { visible: configured, tileWidth: CARD_WIDTH_PX };
  if (root.closest('.bt-nav-drawer')) {
    return { visible: Math.min(MOBILE_VISIBLE_COUNT, itemCount), tileWidth: CARD_WIDTH_PX };
  }

  const menu = root.closest('.bt-mega-menu');
  if (!(menu instanceof HTMLElement)) return { visible: configured, tileWidth: CARD_WIDTH_PX };

  const remaining = leftoverColumns(menu);
  const available = leftoverTilesWidth(menu);
  const target = Math.min(itemCount, remaining);
  if (available <= 0 || target < 1) {
    return { visible: Math.max(1, target), tileWidth: CARD_WIDTH_PX };
  }

  const fitAtMin = Math.max(
    1,
    Math.floor((available + TILE_GAP_PX) / (MIN_CARD_WIDTH_PX + TILE_GAP_PX))
  );
  const visible = Math.min(target, fitAtMin);
  const tileWidth = Math.min(
    CARD_WIDTH_PX,
    Math.max(MIN_CARD_WIDTH_PX, (available - TILE_GAP_PX * (visible - 1)) / visible)
  );
  return { visible, tileWidth };
}

/**
 * @param {HTMLElement} root
 * @param {number} visible
 * @param {number} tileWidth
 */
function setTilesVisibleVar(root, visible, tileWidth = CARD_WIDTH_PX) {
  const track = root.querySelector('[data-bt-nav-tiles-track]');
  const tiles = root.closest('.bt-mega-menu__tiles');
  const value = String(visible);
  const width = `${tileWidth}px`;
  root.style.setProperty('--bt-nav-tiles-visible', value);
  root.style.setProperty('--bt-nav-tile-width', width);
  if (track instanceof HTMLElement) {
    track.style.setProperty('--bt-nav-tiles-visible', value);
  }
  if (tiles instanceof HTMLElement) {
    tiles.style.setProperty('--bt-nav-tiles-visible', value);
    tiles.style.setProperty('--bt-nav-tile-width', width);
    tiles.style.removeProperty('grid-column');
  }
}

/**
 * @param {HTMLElement} root
 */
function initCarousel(root) {
  const track = root.querySelector('[data-bt-nav-tiles-track]');
  const items = visibleTileItems(root);
  if (!(track instanceof HTMLElement) || items.length === 0) {
    root.hidden = true;
    return;
  }

  const inDrawer = Boolean(root.closest('.bt-nav-drawer'));
  const submenu = root.closest('.menu-list__submenu');
  if (submenu instanceof HTMLElement) {
    if (inDrawer && !submenu.classList.contains('is-open')) return;
    if (!inDrawer && getComputedStyle(submenu).visibility === 'hidden') return;
  }
  if (inDrawer) root.classList.add('bt-nav-tiles--mobile');

  const nextBtn = root.querySelector('[data-bt-nav-tiles-next]');
  const prevBtn = root.querySelector('[data-bt-nav-tiles-prev]');
  const dotsHost = root.querySelector('[data-bt-nav-tiles-dots]');
  const controls = root.querySelector('[data-bt-nav-tiles-controls]');
  const slider = root.querySelector('[data-bt-nav-tiles-slider]');

  const state = {
    index: Number(root.dataset.tileIndex) || 0,
    visible: configuredVisibleCount(root),
  };

  const applyLayout = () => {
    const { visible, tileWidth } = desktopTileLayout(root, items.length);
    if (
      visible === state.visible &&
      root.dataset.fitVisible === String(visible) &&
      root.dataset.tileWidth === String(tileWidth) &&
      root.dataset.btNavTilesReady === 'true'
    ) {
      return;
    }
    state.visible = visible;
    root.dataset.tileWidth = String(tileWidth);
    setTilesVisibleVar(root, visible, tileWidth);

    const maxIndex = Math.max(0, items.length - visible);
    const useCarousel = items.length > visible;
    root.classList.toggle('bt-nav-tiles--carousel', useCarousel);
    root.dataset.count = String(items.length);
    root.dataset.fitVisible = String(visible);

    if (!useCarousel) {
      if (controls instanceof HTMLElement) controls.hidden = true;
      if (slider instanceof HTMLElement) slider.hidden = true;
      if (dotsHost instanceof HTMLElement) dotsHost.replaceChildren();
      track.scrollTo({ left: 0 });
      state.index = 0;
      return;
    }

    if (inDrawer) {
      if (controls instanceof HTMLElement) controls.hidden = true;
      if (slider instanceof HTMLElement) slider.hidden = false;
    } else {
      if (controls instanceof HTMLElement) controls.hidden = false;
      if (slider instanceof HTMLElement) slider.hidden = true;
    }

    const pageCount = maxIndex + 1;
    if (dotsHost instanceof HTMLElement && dotsHost.childElementCount !== pageCount) {
      dotsHost.replaceChildren();
      for (let i = 0; i < pageCount; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bt-nav-tiles__dot';
        dot.dataset.btNavTilesDot = '';
        dot.dataset.index = String(i);
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dotsHost.appendChild(dot);
      }
    }

    state.index = Math.min(maxIndex, Math.max(0, state.index));
    goTo(state.index, false);
  };

  /**
   * @param {number} nextIndex
   * @param {boolean} [smooth]
   */
  const goTo = (nextIndex, smooth = true) => {
    const maxIndex = Math.max(0, items.length - state.visible);
    state.index = Math.min(maxIndex, Math.max(0, nextIndex));
    root.dataset.tileIndex = String(state.index);
    const item = items[state.index];
    const origin = items[0];
    if (item instanceof HTMLElement && origin instanceof HTMLElement) {
      track.scrollTo({
        left: item.offsetLeft - origin.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
    root.querySelectorAll('[data-bt-nav-tiles-dot]').forEach((dot, i) => {
      const active = i === state.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (prevBtn instanceof HTMLElement) prevBtn.hidden = state.index <= 0;
    if (nextBtn instanceof HTMLElement) nextBtn.hidden = state.index >= maxIndex;
    if (slider instanceof HTMLElement) {
      const pages = maxIndex + 1;
      const progress = ((state.index + 1) / pages) * 100;
      slider.style.setProperty('--bt-nav-tiles-progress', `${progress}%`);
    }
  };

  applyLayout();

  if (root.dataset.btNavTilesReady === 'true') return;

  root.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-bt-nav-tiles-prev]')) {
      event.preventDefault();
      event.stopPropagation();
      goTo(state.index - 1);
      return;
    }
    if (target.closest('[data-bt-nav-tiles-next]')) {
      event.preventDefault();
      event.stopPropagation();
      goTo(state.index + 1);
      return;
    }
    const dot = target.closest('[data-bt-nav-tiles-dot]');
    if (dot instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      goTo(Number(dot.dataset.index) || 0);
    }
  });

  if (inDrawer && slider instanceof HTMLElement) {
    track.addEventListener(
      'scroll',
      () => {
        const first = items[0];
        if (!(first instanceof HTMLElement) || first.offsetWidth === 0) return;
        const i = Math.round(track.scrollLeft / first.offsetWidth);
        if (i !== state.index) goTo(i, false);
      },
      { passive: true }
    );
  }

  if (!inDrawer) {
    const menu = root.closest('.bt-mega-menu');
    if (menu instanceof HTMLElement && typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => applyLayout());
      observer.observe(menu);
    }
  }

  root.dataset.btNavTilesReady = 'true';
}

/**
 * @param {HTMLElement} drawer
 */
function relocateCards(drawer) {
  const host = drawer.querySelector('[data-bt-nav-cards]');
  if (!(host instanceof HTMLElement) || host.querySelector('[data-bt-nav-card]')) return;
  const items = [...drawer.querySelectorAll('.bt-nav-drawer__list .bt-nav-mobile-collection')].slice(
    0,
    2
  );
  items.forEach((item) => {
    if (!item.querySelector('[data-bt-nav-card]')) return;
    item.hidden = false;
    const parent = item.parentElement;
    const wrap =
      parent instanceof HTMLElement && parent.matches('.shopify-block, shopify-block')
        ? parent
        : item;
    host.appendChild(wrap);
  });
}

/**
 * @param {HTMLElement} drawer
 */
function initDrawer(drawer) {
  if (drawer.dataset.btNavReady === 'true') return;

  relocateCards(drawer);

  const level1 = drawer.querySelector('[data-bt-nav-level="1"]');
  const submenus = /** @type {NodeListOf<HTMLElement>} */ (
    drawer.querySelectorAll('[data-bt-nav-submenu]')
  );

  const headerBack = drawer.querySelector('.bt-nav-drawer__header [data-bt-nav-back]');
  const headerLogo = drawer.querySelector('.bt-nav-drawer__logo');
  const headerEl = drawer.querySelector('.bt-nav-drawer__header');
  const backLabel = drawer.querySelector('[data-bt-nav-back-label]');

  const resetTopbar = () => {
    if (headerBack instanceof HTMLElement) headerBack.hidden = true;
    if (headerLogo instanceof HTMLElement) headerLogo.hidden = false;
    if (backLabel) backLabel.textContent = 'back';
    headerEl?.classList.remove('is-condensed');
  };

  const closeSubmenus = () => {
    submenus.forEach((sub) => {
      sub.hidden = true;
      sub.classList.remove('is-open');
      sub.setAttribute('inert', '');
    });
    drawer.classList.remove('has-submenu-open');
    level1?.classList.remove('is-hidden');
    resetTopbar();
    drawer.querySelectorAll('[data-bt-nav-open-submenu]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
  };

  /**
   * @param {HTMLElement} target
   */
  const openSubmenu = (target) => {
    submenus.forEach((sub) => {
      const open = sub === target;
      sub.hidden = !open;
      sub.classList.toggle('is-open', open);
      if (open) {
        sub.removeAttribute('inert');
        const scroll = sub.querySelector('[data-bt-nav-scroll]');
        if (scroll instanceof HTMLElement) scroll.scrollTop = 0;
        setCondensed(sub, false);
        sub.querySelectorAll(CAROUSEL_SELECTOR).forEach((el) => {
          if (el instanceof HTMLElement) initCarousel(el);
        });
      } else {
        sub.setAttribute('inert', '');
      }
    });
    drawer.classList.add('has-submenu-open');
    if (headerBack instanceof HTMLElement) headerBack.hidden = false;
    if (headerLogo instanceof HTMLElement) headerLogo.hidden = true;
  };

  /**
   * @param {HTMLElement} submenu
   * @param {boolean} condensed
   */
  const setCondensed = (submenu, condensed) => {
    const titleEl = submenu.querySelector('.bt-nav-drawer__category-title');
    const parentTitle = titleEl?.textContent?.trim() ?? '';
    headerEl?.classList.toggle('is-condensed', condensed);
    submenu.querySelector('.bt-nav-l2-chrome')?.classList.toggle('is-condensed', condensed);
    if (headerLogo instanceof HTMLElement) headerLogo.hidden = true;
    if (backLabel) {
      backLabel.textContent = condensed && parentTitle ? `back / ${parentTitle}` : 'back';
    }
  };

  drawer.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openBtn = target.closest('[data-bt-nav-open-submenu]');
    if (openBtn instanceof HTMLElement) {
      event.preventDefault();
      const item = openBtn.closest('.bt-nav-item');
      const panel = item?.querySelector('[data-bt-nav-submenu]');
      if (panel instanceof HTMLElement) {
        openBtn.setAttribute('aria-expanded', 'true');
        openSubmenu(panel);
      }
      return;
    }

    const backBtn = target.closest('[data-bt-nav-back]');
    if (backBtn) {
      event.preventDefault();
      event.stopPropagation();
      closeSubmenus();
    }
  });

  submenus.forEach((sub) => {
    sub.hidden = true;
    const scroll = sub.querySelector('[data-bt-nav-scroll]');
    if (!(scroll instanceof HTMLElement)) return;
    scroll.addEventListener(
      'scroll',
      () => {
        setCondensed(sub, scroll.scrollTop > 24);
      },
      { passive: true }
    );
  });

  const details = drawer.closest('details');
  details?.addEventListener('toggle', () => {
    if (details.open) relocateCards(drawer);
    else closeSubmenus();
  });
  if (details?.open) relocateCards(drawer);

  drawer.dataset.btNavReady = 'true';
}

function boot() {
  document.querySelectorAll(CAROUSEL_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) initCarousel(el);
  });
  document.querySelectorAll(DRAWER_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) initDrawer(el);
  });
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.matches?.(CAROUSEL_SELECTOR)) initCarousel(node);
      node.querySelectorAll?.(CAROUSEL_SELECTOR).forEach((el) => {
        if (el instanceof HTMLElement) initCarousel(el);
      });
    });
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener(
  'pointerenter',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const item = target.closest('.bt-nav-item--dropdown, .menu-list__submenu, .bt-mega-menu');
    if (!item) return;
    const scope = item.closest('.bt-nav-item--dropdown') || item;
    scope.querySelectorAll(CAROUSEL_SELECTOR).forEach((el) => {
      if (el instanceof HTMLElement) initCarousel(el);
    });
  },
  true
);
